<template>
    <div class="container mx-auto px-4 py-8 bg-gray-50">
      <!-- Report Header -->
      <div class="mb-8 text-center">
        <h1 class="text-3xl font-bold mb-4 text-customHafadhColor">تقرير الورد اليومي للدارسين</h1>
      </div>
  
      <!-- Session, Date and Organization Selection -->
      <div class="mb-8">
        <div class="flex justify-center gap-4 items-center flex-wrap">
          <button
            v-if="selectedScheduleId && !loading"
            @click="downloadPDF"
            class="px-3 py-2 bg-customHafadhColor text-white rounded-lg hover:bg-customHafadhColor/90 transition-colors duration-300 flex items-center justify-center"
            :disabled="loading"
            title="تحميل PDF"
          >
            <i class="fas fa-download"></i>
          </button>
          <button
            v-if="selectedScheduleId && !loading"
            @click="downloadAnonymousPDF"
            class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center"
            :disabled="loading"
            title="تحميل PDF مجهول"
          >
            <i class="fas fa-user-secret"></i>
          </button>
           <input
            type="date"
            v-model="selectedDate"
            @change="refetchIfReady"
            class="block px-4 py-2 border-2 border-customHafadhColor rounded-lg focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
          />
          <select
            v-model="selectedScheduleId"
            @change="handleScheduleChange"
            class="block w-64 px-4 py-2 bg-white border-2 border-customHafadhColor rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor">
            <option value="">اختر الجدول</option>
            <option v-for="schedule in scheduleOptions" :key="schedule.id" :value="schedule.id">
              {{ schedule.title }}
            </option>
          </select>

          <select
            v-if="selectedScheduleId"
            v-model="selectedTeacherId"
            @change="handleTeacherChange"
            :disabled="teachersLoading"
            class="block w-64 px-4 py-2 bg-white border-2 border-customHafadhColor rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor disabled:opacity-60">
            <option value="">كل المعلمين</option>
            <option v-for="teacher in teacherOptions" :key="teacher.id" :value="teacher.id">
              {{ teacher.name }}
            </option>
          </select>

          <select
            v-model="selectedOrganizationId"
            class="block w-64 px-4 py-2 bg-white border-2 border-customHafadhColor rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor">
            <option value="">اختر الجهة</option>
            <option v-for="org in organizations" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>
      </div>
  
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-customHafadhColor border-t-transparent"></div>
        <p class="mt-4 text-lg text-customHafadhColor">جاري التحميل...</p>
      </div>
  
      <!-- Error State -->
      <div v-if="error" class="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg relative text-right mb-4">
        <strong class="font-bold ml-2">خطأ!</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>
  
      <!-- Report Content -->
      <div v-if="report && !loading" class="daily-report">
        <!-- PDF Header with Logos (hidden on screen, visible in PDF) -->
        <div class="pdf-header" style="display: none;">
          <div class="header-logos">
            <div class="left-logo">
              <!-- App Logo -->
              <img :src="appLogo" alt="شعار التطبيق" class="app-logo" />
            </div>
            <div class="center-title">
              <h1 class="pdf-title">تقرير الورد اليومي للدارسين</h1>
              <p class="pdf-subtitle">{{ formattedDate }} - {{ selectedScheduleTitle }}<span v-if="selectedTeacherName"> - {{ selectedTeacherName }}</span></p>
            </div>
            <div class="right-logo">
              <img v-if="selectedOrganization && (selectedOrganization.logoUrl || selectedOrganization.logoBase64)" 
                   :src="selectedOrganization.logoUrl || `data:image/jpeg;base64,${selectedOrganization.logoBase64}`" 
                   :alt="selectedOrganization.name"
                   class="organization-logo" />
            </div>
          </div>
          <hr class="header-divider" />
        </div>
        
        <div class="report-content">
          <!-- Summary Section -->
          <div class="summary-section grid grid-cols-6 gap-3 mb-6">
            <div class="summary-box text-center bg-gradient-to-br from-blue-600 to-blue-700 p-3 shadow-md border border-blue-300">
              <h3 class="text-lg font-semibold text-white mb-1">مجموع الأجزاء</h3>
              <p class="text-3xl font-bold text-white">{{ report.totalParts || 0.0 }}</p>
            </div>
            <div class="summary-box text-center bg-gradient-to-br from-green-600 to-green-700 p-3 shadow-md border border-green-300">
              <h3 class="text-lg font-semibold text-white mb-1">مجموع الأوجه</h3>
              <p class="text-3xl font-bold text-white">{{ report.totalPages || 0 }}</p>
            </div>
            <div class="summary-box text-center bg-gradient-to-br from-purple-600 to-purple-700 p-3 shadow-md border border-purple-300">
              <h3 class="text-lg font-semibold text-white mb-1">نسبة الإنجاز</h3>
              <p class="text-3xl font-bold text-white">{{ completionPercentage }}%</p>
            </div>
            <div class="summary-box text-center bg-gradient-to-br from-orange-600 to-orange-700 p-3 shadow-md border border-orange-300">
              <h3 class="text-lg font-semibold text-white mb-1">عدد المدخلين</h3>
              <p class="text-3xl font-bold text-white">{{ dataEntryCount }}</p>
            </div>
            <div class="summary-box text-center bg-gradient-to-br from-sky-600 to-sky-700 p-3 shadow-md border border-sky-300">
              <h3 class="text-lg font-semibold text-white mb-1">عدد المُقيَّمين</h3>
              <p class="text-3xl font-bold text-white">{{ evaluatedCount }}</p>
            </div>
            <div class="summary-box text-center bg-gradient-to-br from-teal-600 to-teal-700 p-3 shadow-md border border-teal-300">
              <h3 class="text-lg font-semibold text-white mb-1">عدد الدارسين</h3>
              <p class="text-3xl font-bold text-white">{{ report.studentCount || 0 }}</p>
            </div>
          </div>
  
          <!-- Daily Recitation Report Section -->
          <div class="recitation-section bg-white p-4 shadow-lg border border-gray-300 rounded-lg">
            <h3 class="text-3xl font-bold mb-4 text-center text-customHafadhColor bg-gray-50 py-2 rounded-md">تفاصيل الورد اليومي</h3>
            <div class="table-container">
              <table class="min-w-full divide-y divide-gray-300 border border-gray-300 rounded-lg overflow-hidden">
                <thead>
                  <tr class="bg-gradient-to-r from-customHafadhColor to-customHafadhColor/90">
                    <th class="px-4 py-4 text-right font-bold text-white border-l border-white/20 w-1/2" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.43rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">الاسم</th>
                    <th class="px-4 py-4 text-center font-bold text-white border-l border-white/20 w-1/4" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.15rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">الورد اليومي<br /><span class="th-sub">(إدخال الدارس)</span></th>
                    <th class="px-4 py-4 text-center font-bold text-white w-1/4" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.15rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">التسميع في الجلسة<br /><span class="th-sub">(تقييم المعلم)</span></th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(student, index) in report.studentReports" :key="student.studentId"
                      :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                      class="hover:bg-blue-50 transition-colors duration-200">
                    <td class="px-4 py-2 text-right text-gray-800 font-medium border-l border-gray-200" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.43rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">{{ student.studentName }}</td>
                    <td class="px-4 py-2 text-center text-gray-800 font-semibold border-l border-gray-200">
                      <span
                        class="inline-block px-2 py-1 rounded-full"
                        :class="wirdAmount(student) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                        style="font-size: 1.3rem;"
                      >
                        {{ wirdAmount(student) || 'لم يتم ادخال ورد' }}
                      </span>
                      <div v-if="student.wirdEntriesCount > 1" class="wird-breakdown">
                        ({{ student.wirdEntriesCount }} إدخالات: {{ wirdBreakdown(student) }})
                      </div>
                    </td>
                    <td class="px-4 py-2 text-center text-gray-800 font-semibold">
                      <span
                        class="inline-block px-2 py-1 rounded-full"
                        :class="student.hasEvaluation ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'"
                        style="font-size: 1.3rem;"
                      >
                        {{ student.hasEvaluation ? teacherPages(student) : 'لم يُقيَّم' }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!report.studentReports || report.studentReports.length === 0">
                    <td colspan="3" class="px-4 py-8 text-center text-gray-500 italic">لا يوجد بيانات لهذا اليوم</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
  
           <!-- Additional Summary Section -->
           <div class="summary-section grid grid-cols-2 gap-6">
             
           </div>
        </div>
      </div>
      <div v-else-if="!loading && !error" class="text-center py-12 text-gray-500 text-lg">
          الرجاء اختيار جدول وتاريخ لعرض التقرير.
        </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
import url from '@/url'; // Assuming you have a file with API endpoints
import html2pdf from 'html2pdf.js';
import appLogo from '@/assets/logo.png';
import { listIqraSchedules, listIqraSlotTeachers } from '@/services/iqraSchedules';

  // The backend resolves "today" in APP_TIMEZONE (Asia/Riyadh). Using
  // toISOString() here would send the UTC date and request the previous day
  // for the first hours after local midnight. 'en-CA' formats as YYYY-MM-DD.
  const TENANT_TZ = process.env.VUE_APP_TENANT_TIMEZONE || 'Asia/Riyadh';
  const tenantToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: TENANT_TZ }).format(new Date());

  export default {
    name: 'DailyStudentReport',
    data() {
      return {
        scheduleOptions: [],
        teacherOptions: [],
        organizations: [],
        selectedScheduleId: '',
        selectedTeacherId: '',
        selectedOrganizationId: '',
        selectedDate: tenantToday(),
        report: null,
        loading: false,
        teachersLoading: false,
        error: null,
        appLogo: appLogo,
      };
    },
    computed: {
      selectedScheduleTitle() {
        if (!this.selectedScheduleId) return '';
        const sch = this.scheduleOptions.find((s) => String(s.id) === String(this.selectedScheduleId));
        return sch ? sch.title : '';
      },
      selectedTeacherName() {
        if (!this.selectedTeacherId) return '';
        const t = this.teacherOptions.find((teacher) => String(teacher.id) === String(this.selectedTeacherId));
        return t ? t.name : '';
      },
      selectedOrganization() {
        return this.organizations.find(org => org.id === this.selectedOrganizationId);
      },
      formattedDate() {
        // selectedDate is already 'YYYY-MM-DD'. Re-parsing it with `new Date()`
        // treats it as UTC midnight and renders the PREVIOUS day west of UTC.
        return this.selectedDate || '';
      },
      dataEntryCount() {
        if (!this.report) return 0;
        // The backend now counts students whose own daily wird > 0. Fall back to a
        // local recount only if an older API build is deployed.
        if (this.report.dataEntryCount != null) return Number(this.report.dataEntryCount) || 0;
        return (this.report.studentReports || []).filter(s => this.wirdAmount(s) > 0).length;
      },
      evaluatedCount() {
        if (!this.report) return 0;
        if (this.report.evaluatedCount != null) return Number(this.report.evaluatedCount) || 0;
        return (this.report.studentReports || []).filter(s => s.hasEvaluation).length;
      },
      completionPercentage() {
        if (this.report && this.report.completionPercentage != null) return this.report.completionPercentage;
        const total = (this.report && this.report.studentReports) ? this.report.studentReports.length : 0;
        if (!total) return '0.0';
        return ((this.dataEntryCount / total) * 100).toFixed(1);
      }
    },
    methods: {
      // الورد اليومي: مجموع كل إدخالات الدارس في ذلك اليوم (يحسبه الخادم).
      wirdAmount(student) {
        return Number(student && student.selfReviewCount) || 0;
      },
      teacherPages(student) {
        return Number(student && student.teacherPages) || 0;
      },
      // «5 + 10» — يوضّح أن الرقم مجموع عدة إدخالات لا إدخالاً واحداً.
      wirdBreakdown(student) {
        const entries = (student && student.wirdEntries) || [];
        return entries.map(e => Number(e.amount) || 0).join(' + ');
      },
      async loadScheduleOptions() {
        try {
          const result = await listIqraSchedules({ is_active: true, page: 1, size: 100 });
          const data = result?.data ?? [];
          this.scheduleOptions = (Array.isArray(data) ? data : []).map((s) => ({
            id: String(s.id),
            title: s.title || `جدول #${s.id}`,
          }));
        } catch (err) {
          this.error = err?.message || 'حدث خطأ أثناء تحميل الجداول';
          console.error('Error loading schedules:', err);
        }
      },
      async fetchOrganizations() {
        try {
          const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.organizations, {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          });
          if (response.data?.result?.data) {
            this.organizations = response.data.result.data;
          }
        } catch (err) {
          console.error('Error fetching organizations:', err);
          // Don't show error for organizations as it's optional
        }
      },
      async loadTeacherOptions() {
        // Reset teacher selection whenever the schedule changes.
        this.selectedTeacherId = '';
        this.teacherOptions = [];
        if (!this.selectedScheduleId) return;

        this.teachersLoading = true;
        try {
          const rows = await listIqraSlotTeachers({
            schedule_id: this.selectedScheduleId,
            is_active: true,
          });
          const list = Array.isArray(rows) ? rows : (rows?.data ?? []);
          // A schedule has many slot-teacher rows; collapse to a distinct teacher list.
          const seen = new Map();
          list.forEach((row) => {
            if (row.teacher_id == null) return;
            const id = String(row.teacher_id);
            if (!seen.has(id)) {
              seen.set(id, { id, name: row.teacher_name || `معلم #${id}` });
            }
          });
          this.teacherOptions = Array.from(seen.values());
        } catch (err) {
          console.error('Error loading teachers for schedule:', err);
          this.teacherOptions = [];
        } finally {
          this.teachersLoading = false;
        }
      },
      async handleScheduleChange() {
        // Schedule changed: rebuild the teacher list (and reset to "all teachers"), then refetch.
        await this.loadTeacherOptions();
        this.refetchIfReady();
      },
      handleTeacherChange() {
        this.refetchIfReady();
      },
      refetchIfReady() {
        if (!this.selectedScheduleId || !this.selectedDate) {
          this.clearReport();
          return;
        }
        this.fetchReport();
      },
      clearReport() {
        this.report = null;
        this.error = null;
      },
      async fetchReport() {
        this.loading = true;
        this.error = null;
        try {
          // Call the iqra daily student report endpoint
          const response = await axios.get(
            process.env.VUE_APP_SERVICE_URL + url.iqra_daily_student_report,
            {
              headers: {
                'X-Authorization': localStorage.getItem('token')
              },
               params: {
                 scheduleId: this.selectedScheduleId,
                 ...(this.selectedTeacherId ? { teacherId: this.selectedTeacherId } : {}),
                 date: this.selectedDate
               }
            }
          );
          if (response.data?.result?.data) {
            this.report = response.data.result.data;
          } else {
             this.report = null; // Clear previous data if no data in response
          }
        } catch (err) {
          this.error = err.response?.data?.mesAr || 'حدث خطأ أثناء تحميل التقرير اليومي';
          console.error('Error fetching daily report:', err);
          this.report = null; // Clear report on error
        } finally {
          this.loading = false;
        }
      },
      async downloadPDF() {
        try {
          const element = document.querySelector('.daily-report');
          
          // Show the PDF header for PDF generation
          const pdfHeader = element.querySelector('.pdf-header');
          if (pdfHeader) {
            pdfHeader.style.display = 'block';
          }
          
          // Use Arabic schedule name and hijri date in filename
          const scheduleName = this.selectedScheduleTitle ? this.selectedScheduleTitle.replace(/[^\u0600-\u06FF\w\s]/g, '') : 'الجدول';
          const teacherName = this.selectedTeacherName ? this.selectedTeacherName.replace(/[^\u0600-\u06FF\w\s]/g, '') : '';
          const orgName = this.selectedOrganization ? this.selectedOrganization.name.replace(/[^\u0600-\u06FF\w\s]/g, '') : '';
          const baseFilename = `تقرير_الورد_اليومي_${scheduleName}${teacherName ? "_" + teacherName : ""}_${this.formattedDate}${orgName ? '_' + orgName : ''}`;
          const safeFilename = baseFilename.replace(/[^\u0600-\u06FF\w\s-]/g, '').trim() + '.pdf';
          
          // High resolution PDF configuration
          const opt = {
            margin: [0.3, 0.3, 0.3, 0.3],
            filename: safeFilename,
            image: { 
              type: 'jpeg', 
              quality: 1.0  // Maximum quality
            },
            html2canvas: { 
              scale: 3,  // Much higher scale for FHD quality
              useCORS: false,
              allowTaint: false,
              scrollX: 0,
              scrollY: 0,
              windowWidth: 1920,  // FHD width
              windowHeight: 1080, // FHD height
              backgroundColor: '#ffffff',
              dpi: 300,  // High DPI for print quality
              letterRendering: true,  // Better text rendering
              proxy: undefined,
              foreignObjectRendering: false,
              logging: false,
              imageTimeout: 15000,  // Longer timeout for high-res rendering
              removeContainer: false
            },
            jsPDF: { 
              unit: 'mm',  // Use mm for higher precision
              format: 'a4', 
              orientation: 'portrait',
              compress: false,  // No compression for maximum quality
              precision: 16  // Higher precision
            },
            pagebreak: {
              mode: ['avoid-all'],
              avoid: ['.pdf-header', '.summary-section', '.recitation-section']
            }
          };

          // Add temporary class for PDF styling
          element.classList.add('pdf-export');
          
          // Small delay for rendering
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Try different approach for secure download
          try {
            // Method 1: Direct save (should work for most cases)
            await html2pdf().set(opt).from(element).save();
          } catch (saveError) {
            console.warn('Direct save failed, trying blob method:', saveError);
            // Method 2: Blob fallback if direct save fails
            const pdf = await html2pdf().set(opt).from(element).outputPdf('dataurlstring');
            const link = document.createElement('a');
            link.download = safeFilename;
            link.href = pdf;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          
          // Hide the PDF header again and remove temporary class
          if (pdfHeader) {
            pdfHeader.style.display = 'none';
          }
          element.classList.remove('pdf-export');
          
        } catch (error) {
          console.error('Error generating PDF:', error);
          this.error = 'حدث خطأ أثناء تحميل الملف: ' + error.message;
          
          // Make sure to cleanup even if error occurs
          const element = document.querySelector('.daily-report');
          const pdfHeader = element?.querySelector('.pdf-header');
          if (pdfHeader) {
            pdfHeader.style.display = 'none';
          }
          if (element) {
            element.classList.remove('pdf-export');
          }
        }
      },
      convertToArabicNumber(num) {
        const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
        return num.toString().split('').map(digit => arabicNumbers[parseInt(digit)]).join('');
      },
      async downloadAnonymousPDF() {
        try {
          // Create a backup of original student reports
          const originalReports = this.report.studentReports;
          
          // Replace names with anonymous ones
          this.report.studentReports = originalReports.map((student, index) => ({
            ...student,
            studentName: `الدارس ${this.convertToArabicNumber(index + 1)}`
          }));
          
          const element = document.querySelector('.daily-report');
          
          // Show the PDF header for PDF generation
          const pdfHeader = element.querySelector('.pdf-header');
          if (pdfHeader) {
            pdfHeader.style.display = 'block';
          }
          
          const dateStr = this.formattedDate.replace(/-/g, '');
          
          // High resolution PDF configuration
          const opt = {
            margin: [0.3, 0.3, 0.3, 0.3],
            filename: `dailyreport_anonymous_${dateStr}.pdf`,
            image: { 
              type: 'jpeg', 
              quality: 1.0  // Maximum quality
            },
            html2canvas: { 
              scale: 3,  // Much higher scale for FHD quality
              useCORS: false,
              allowTaint: false,
              scrollX: 0,
              scrollY: 0,
              windowWidth: 1920,  // FHD width
              windowHeight: 1080, // FHD height
              backgroundColor: '#ffffff',
              dpi: 300,  // High DPI for print quality
              letterRendering: true,  // Better text rendering
              proxy: undefined,
              foreignObjectRendering: false,
              logging: false,
              imageTimeout: 15000,  // Longer timeout for high-res rendering
              removeContainer: false
            },
            jsPDF: { 
              unit: 'mm',  // Use mm for higher precision
              format: 'a4', 
              orientation: 'portrait',
              compress: false,  // No compression for maximum quality
              precision: 16  // Higher precision
            },
            pagebreak: {
              mode: ['avoid-all'],
              avoid: ['.pdf-header', '.summary-section', '.recitation-section']
            }
          };

          // Add temporary class for PDF styling
          element.classList.add('pdf-export');
          
          // Small delay for rendering
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Try different approach for secure download
          try {
            // Method 1: Direct save (should work for most cases)
            await html2pdf().set(opt).from(element).save();
          } catch (saveError) {
            console.warn('Direct save failed, trying blob method:', saveError);
            // Method 2: Blob fallback if direct save fails
            const pdf = await html2pdf().set(opt).from(element).outputPdf('dataurlstring');
            const link = document.createElement('a');
            link.download = `dailyreport_anonymous_${dateStr}.pdf`;
            link.href = pdf;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          
          // Hide the PDF header again and remove temporary class
          if (pdfHeader) {
            pdfHeader.style.display = 'none';
          }
          element.classList.remove('pdf-export');
          
          // Restore original student names
          this.report.studentReports = originalReports;
          
        } catch (error) {
          console.error('Error generating anonymous PDF:', error);
          this.error = 'حدث خطأ أثناء تحميل الملف المجهول: ' + error.message;
          
          // Make sure to cleanup and restore original names
          const element = document.querySelector('.daily-report');
          const pdfHeader = element?.querySelector('.pdf-header');
          if (pdfHeader) {
            pdfHeader.style.display = 'none';
          }
          if (element) {
            element.classList.remove('pdf-export');
          }
          
          // Restore original student names in case of error
          if (this.report && this.report.studentReports) {
            // This will be handled by the backup we created
          }
        }
      }
    },
    created() {
      // Fetch schedules and organizations when the component is created
      this.loadScheduleOptions();
      this.fetchOrganizations();
    }
  };
  </script>
  
  <style scoped>
  .daily-report {
    direction: rtl;
    text-align: right;
  }
  
  .summary-section .summary-box {
    transition: transform 0.2s ease-in-out;
  }
  
  .summary-section .summary-box:hover {
    transform: translateY(-5px);
  }
  
  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin: 20px 0;
    direction: rtl;
  }
  
  th, td {
    direction: rtl;
    unicode-bidi: bidi-override;
  }
  
  th {
    font-weight: 600;
    letter-spacing: 0.05em;
  }
  
  td {
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  tbody tr:last-child td {
    border-bottom: none;
  }
  
  .text-customHafadhColor {
    color: #c9a06c;
  }
  
  h1, h2, h3, h4 {
    font-family: 'Tajawal', sans-serif;
  }
  
  /* Custom scrollbar for the table
  .recitation-section {
    max-height: 600px;
    overflow-y: auto;
  }
   */
  .recitation-section::-webkit-scrollbar {
    width: 8px;
  }
  
  .recitation-section::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .recitation-section::-webkit-scrollbar-thumb {
    background: #c9a06c;
    border-radius: 4px;
  }
  
  .recitation-section::-webkit-scrollbar-thumb:hover {
    background: #b08d5d;
  }
  
  .summary-box {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .summary-box::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
    pointer-events: none;
  }
  
  .summary-box:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(201, 160, 108, 0.2);
  }

  /* Column header qualifier — «(إدخال الدارس)» / «(تقييم المعلم)» */
  .th-sub {
    font-size: 0.78em;
    font-weight: 500;
    opacity: 0.9;
  }

  /* Shows that a wird total is the sum of several submissions: «(3 إدخالات: 5 + 10 + 6)» */
  .wird-breakdown {
    margin-top: 4px;
    font-size: 0.8rem;
    color: #6b7280;
    direction: rtl;
  }

  /* PDF Export Styles */
  .pdf-export {
    direction: rtl;
    padding: 15px;
    background: white;
    font-size: 12px;
  }
  
  .pdf-export .pdf-header {
    display: block !important;
    margin-bottom: 15px;
  }
  
  .pdf-export .summary-section {
    break-inside: avoid;
    page-break-inside: avoid;
    margin-bottom: 12px;
    gap: 8px !important;
  }
  
  .pdf-export .summary-box {
    padding: 6px !important;
    font-size: 10px;
    border-radius: 6px !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
  }
  
  .pdf-export .summary-box h3 {
    font-size: 13px !important;
    margin-bottom: 2px !important;
    font-weight: 600 !important;
  }
  
  .pdf-export .summary-box p {
    font-size: 18px !important;
    font-weight: bold;
    margin: 0 !important;
  }
  
  /* PDF Summary Box Colors */
  .pdf-export .summary-box:nth-child(1) {
    background: linear-gradient(135deg, #2563eb, #1d4ed8) !important;
    border: 1px solid #3b82f6 !important;
  }
  
  .pdf-export .summary-box:nth-child(2) {
    background: linear-gradient(135deg, #059669, #047857) !important;
    border: 1px solid #10b981 !important;
  }
  
  .pdf-export .summary-box:nth-child(3) {
    background: linear-gradient(135deg, #7c3aed, #6d28d9) !important;
    border: 1px solid #8b5cf6 !important;
  }
  
  .pdf-export .summary-box:nth-child(4) {
    background: linear-gradient(135deg, #ea580c, #dc2626) !important;
    border: 1px solid #f97316 !important;
  }
  
  /* 5 = عدد المُقيَّمين (teacher evaluations) */
  .pdf-export .summary-box:nth-child(5) {
    background: linear-gradient(135deg, #0284c7, #0369a1) !important;
    border: 1px solid #38bdf8 !important;
  }

  /* 6 = عدد الدارسين */
  .pdf-export .summary-box:nth-child(6) {
    background: linear-gradient(135deg, #0d9488, #0f766e) !important;
    border: 1px solid #14b8a6 !important;
  }

  .pdf-export .recitation-section {
    break-inside: avoid;
    page-break-inside: avoid;
    padding: 12px !important;
    margin-bottom: 0;
    border: 1px solid #d1d5db !important;
    border-radius: 8px !important;
    background: white !important;
  }
  
  .pdf-export .recitation-section h3 {
    font-size: 23px !important;
    margin-bottom: 8px !important;
    background: #f9fafb !important;
    padding: 6px !important;
    border-radius: 6px !important;
    color: #c9a06c !important;
    font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif !important;
    direction: rtl !important;
    unicode-bidi: bidi-override !important;
  }
  
  .pdf-export .table-container {
    border-radius: 6px !important;
    overflow: hidden !important;
  }
  
  .pdf-export table {
    margin: 8px 0 !important;
    font-size: 10px;
    border: 2px solid #c9a06c !important;
    border-radius: 6px !important;
    direction: rtl !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
  }
  
  .pdf-export th {
    padding: 8px !important;
    font-size: 17px !important;
    background: linear-gradient(135deg, #c9a06c, #b08d5d) !important;
    color: white !important;
    font-weight: 700 !important;
    direction: rtl !important;
    unicode-bidi: bidi-override !important;
    text-align: center !important;
    font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif !important;
    letter-spacing: 0px !important;
    writing-mode: horizontal-tb !important;
    border: 1px solid #b08d5d !important;
  }
  
  .pdf-export td {
    padding: 6px 8px !important;
    font-size: 13px !important;
    direction: rtl !important;
    unicode-bidi: bidi-override !important;
    font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif !important;
    writing-mode: horizontal-tb !important;
    border-bottom: 1px solid #e5e7eb !important;
    border-left: 1px solid #e5e7eb !important;
  }
  
  .pdf-export td span {
    background: #dbeafe !important;
    color: #1e40af !important;
    padding: 1px 4px !important;
    border-radius: 12px !important;
    font-size: 12px !important;
    font-weight: 600 !important;
  }
  
  /* Column 2 = the student's own daily wird (the headline number) */
  .pdf-export td:nth-child(2) span {
    background: #dcfce7 !important;
    color: #166534 !important;
  }

  /* Column 3 = in-session recitation evaluated by the teacher */
  .pdf-export td:nth-child(3) span {
    background: #dbeafe !important;
    color: #1e40af !important;
  }

  .pdf-export .th-sub {
    font-size: 12px !important;
  }

  .pdf-export .wird-breakdown {
    font-size: 10px !important;
  }

  .pdf-export tbody tr:nth-child(even) {
    background: #f9fafb !important;
  }
  
  .pdf-export tbody tr:nth-child(odd) {
    background: white !important;
  }
  
  .pdf-export tbody tr:last-child td {
    border-bottom: none !important;
  }
  
  .pdf-export td:last-child {
    border-left: none !important;
  }
  
  .pdf-export th:last-child {
    border-left: none !important;
  }
  
  .report-content, .pdf-export .report-content {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  
  /* PDF Header Styles */
  .pdf-header {
    direction: rtl;
    margin-bottom: 15px;
  }
  
  .header-logos {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    min-height: 60px;
  }
  
  .left-logo, .right-logo {
    flex: 0 0 100px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .center-title {
    flex: 1;
    text-align: center;
    padding: 0 15px;
  }
  
  .organization-logo, .app-logo {
    max-width: 80px;
    max-height: 80px;
    object-fit: contain;
    border-radius: 8px;
  }
  
  .pdf-export .organization-logo, .pdf-export .app-logo {
    max-width: 60px;
    max-height: 60px;
  }
  
  .pdf-title {
    font-size: 20px;
    font-weight: bold;
    color: #c9a06c;
    margin: 0 0 6px 0;
    font-family: 'Tajawal', sans-serif;
  }
  
  .pdf-export .pdf-title {
    font-size: 26px !important;
    margin: 0 0 4px 0 !important;
    font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif !important;
    direction: rtl !important;
    unicode-bidi: bidi-override !important;
  }
  
  .pdf-subtitle {
    font-size: 14px;
    color: #666;
    margin: 0 0 3px 0;
    font-family: 'Tajawal', sans-serif;
  }
  
  .pdf-export .pdf-subtitle {
    font-size: 17px !important;
    margin: 0 !important;
    font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif !important;
    direction: rtl !important;
    unicode-bidi: bidi-override !important;
  }
  
  .pdf-organization {
    font-size: 12px;
    color: #888;
    margin: 0;
    font-family: 'Tajawal', sans-serif;
  }
  
  .header-divider {
    border: none;
    border-top: 1.5px solid #c9a06c;
    margin: 8px 0;
  }
  
  .pdf-export .header-divider {
    margin: 6px 0 !important;
  }
  
  /* Add Font Awesome if not already included in your project */
  @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');
  </style>